import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["title", "slug", "activeIngredientId", "activeIngredientName", "activeIngredientResults"]

  connect() {
    this.slugManuallyEdited = this.slugTarget.value.length > 0
    this.searchTimeout = null
    this._searchController = null
    this.closeResults = this.closeResults.bind(this)
    document.addEventListener("click", this.closeResults)
  }

  disconnect() {
    clearTimeout(this.searchTimeout)
    this._searchController?.abort()
    this._searchController = null
    document.removeEventListener("click", this.closeResults)
  }

  titleChanged() {
    if (this.slugManuallyEdited) return
    this.slugTarget.value = this.parameterize(this.titleTarget.value)
  }

  slugEdited() {
    this.slugManuallyEdited = true
    this.slugTarget.value = this.parameterize(this.slugTarget.value)
  }

  activeIngredientChanged() {
    const value = this.activeIngredientNameTarget.value.trim()

    clearTimeout(this.searchTimeout)

    this.activeIngredientIdTarget.value = ""

    if (value.length < 2) {
      this.searchActiveIngredients(value)
      return
    }

    this.searchTimeout = setTimeout(() => this.searchActiveIngredients(value), 200)
  }

  async searchActiveIngredients(query) {
    this._searchController?.abort()
    this._searchController = null

    if (query.length < 2) {
      this.renderActiveIngredientResults([])
      if (query.length === 0) this.activeIngredientIdTarget.value = ""
      return
    }

    const searchController = new AbortController()
    this._searchController = searchController

    try {
      const response = await fetch(`/admin/active_ingredients/search?q=${encodeURIComponent(query)}`, {
        headers: { Accept: "application/json" },
        signal: searchController.signal,
      })

      if (!response.ok) throw new Error(`Active ingredient search failed: ${response.status}`)

      const results = await response.json()
      if (this._searchController === searchController) {
        this._searchController = null
        this.renderActiveIngredientResults(results)
      }
    } catch (error) {
      if (error.name === "AbortError") return

      if (this._searchController === searchController) {
        this._searchController = null
        this.renderActiveIngredientResults([])
      }
    }
  }

  selectActiveIngredient(event) {
    const result = event.currentTarget
    this.activeIngredientNameTarget.value = result.dataset.activeIngredientLabel
    this.activeIngredientIdTarget.value = result.dataset.activeIngredientId
    this.renderActiveIngredientResults([])
  }

  renderActiveIngredientResults(results) {
    this.activeIngredientResultsTarget.innerHTML = ""

    if (results.length === 0) {
      this.activeIngredientResultsTarget.classList.add("hidden")
      return
    }

    results.forEach((activeIngredient) => {
      const item = document.createElement("li")
      const button = document.createElement("button")
      const label = activeIngredient.label || activeIngredient.name

      button.type = "button"
      button.className = "block w-full px-4 py-2 text-left text-sm hover:bg-brand-primary-subtle focus:bg-brand-primary-subtle focus:outline-none"
      button.dataset.action = "click->blog-post-form#selectActiveIngredient"
      button.dataset.activeIngredientId = activeIngredient.id
      button.dataset.activeIngredientLabel = label
      button.textContent = label

      item.appendChild(button)
      this.activeIngredientResultsTarget.appendChild(item)
    })

    this.activeIngredientResultsTarget.classList.remove("hidden")
  }

  closeResults(event) {
    if (this.element.contains(event.target)) return
    this.activeIngredientResultsTarget.classList.add("hidden")
  }

  parameterize(value) {
    return value
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
  }
}
